import type { PriceRank } from '@/models/room-rate/price-rank'

export type BaseRatePriceType = 'rc' | 'perPerson'
export type BaseRateChargeType = 'rc' | 'per_person'
export type BaseRateGuestCount = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10
export type BaseRatePaxBand = BaseRateGuestCount

export type BaseRateRoomType = {
  id: string
  name: string
  adoptedChargeType: BaseRateChargeType | null
}

export type BaseRatePriceCell = {
  priceRankId: string
  price: number | null
}

export type BaseRateRow = {
  roomTypeId: string
  name: string
  adoptedChargeType: BaseRateChargeType | null
  prices: BaseRatePriceCell[]
}

/** roomTypeId -> priceRankId -> amount (null = unset) */
export type BaseRateAmountMap = Record<string, Record<string, number | null>>

export type BaseRateFetchParams = {
  sheetChargeType: BaseRateChargeType
  paxBand?: BaseRatePaxBand
}

export type BaseRateUpdateInput = {
  sheetChargeType: BaseRateChargeType
  paxBand?: BaseRatePaxBand
  prices: {
    roomTypeId: string
    priceRankId: string
    price: number | null
  }[]
  /** 指定時は施設の全部屋タイプに同じ値を保存。省略時は採用を変えない。FE は保存シートの sheetChargeType を送る */
  adoptedChargeType?: BaseRateChargeType
}

export const GUEST_COUNT_OPTIONS: BaseRateGuestCount[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]

/** タブ切替後も最後に見ていた料金区分を復元するための UI 状態 */
export const BASE_RATE_VIEW_STORAGE_KEY = 'base-rate-view-prefs'

export type BaseRateViewPrefs = {
  priceType: BaseRatePriceType
  guestCount: BaseRateGuestCount
}

const isBaseRatePriceType = (value: unknown): value is BaseRatePriceType => value === 'rc' || value === 'perPerson'

const isBaseRateGuestCount = (value: unknown): value is BaseRateGuestCount =>
  typeof value === 'number' && GUEST_COUNT_OPTIONS.includes(value as BaseRateGuestCount)

export const loadBaseRateViewPrefs = (): BaseRateViewPrefs | null => {
  try {
    const raw = sessionStorage.getItem(BASE_RATE_VIEW_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<BaseRateViewPrefs>
    if (isBaseRatePriceType(parsed.priceType) && isBaseRateGuestCount(parsed.guestCount)) {
      return { priceType: parsed.priceType, guestCount: parsed.guestCount }
    }
  } catch {
    // 壊れた sessionStorage は無視
  }
  return null
}

export const saveBaseRateViewPrefs = (prefs: BaseRateViewPrefs) => {
  try {
    sessionStorage.setItem(BASE_RATE_VIEW_STORAGE_KEY, JSON.stringify(prefs))
  } catch {
    // quota / プライベートモード等は非永続化のみ
  }
}

/** 客室設定で名前の先頭が半角 _ の部屋タイプは架空部屋。一覧には出さない。 */
export const isFictitiousRoomTypeName = (name: string): boolean => name.startsWith('_')

export const toPriceTypeFromAdopted = (adopted: BaseRateChargeType | null | undefined): BaseRatePriceType =>
  adopted === 'per_person' ? 'perPerson' : 'rc'

export const resolveAdoptedPriceType = (rows: BaseRateRow[]): BaseRatePriceType => {
  const adopted = rows.find(row => row.adoptedChargeType)?.adoptedChargeType
  return toPriceTypeFromAdopted(adopted)
}

export const toApiSheetChargeType = (priceType: BaseRatePriceType): BaseRateChargeType => (priceType === 'perPerson' ? 'per_person' : 'rc')

export const toApiPaxBand = (guestCount: BaseRateGuestCount): BaseRatePaxBand => guestCount

export const createEmptyAmountMap = (roomTypes: BaseRateRoomType[], ranks: PriceRank[]): BaseRateAmountMap => {
  const map: BaseRateAmountMap = {}
  roomTypes.forEach(room => {
    map[room.id] = {}
    ranks.forEach(rank => {
      map[room.id][rank.id] = null
    })
  })
  return map
}

export const cloneAmountMap = (map: BaseRateAmountMap): BaseRateAmountMap =>
  Object.keys(map).reduce<BaseRateAmountMap>((acc, roomId) => {
    acc[roomId] = { ...map[roomId] }
    return acc
  }, {})

export const toRoomTypes = (rows: BaseRateRow[]): BaseRateRoomType[] =>
  rows
    .filter(row => !isFictitiousRoomTypeName(row.name))
    .map(row => ({
      id: row.roomTypeId,
      name: row.name,
      adoptedChargeType: row.adoptedChargeType ?? null,
    }))

export const toAmountMap = (rows: BaseRateRow[], ranks: PriceRank[]): BaseRateAmountMap => {
  const map = createEmptyAmountMap(toRoomTypes(rows), ranks)
  const rankIds = new Set(ranks.map(rank => rank.id))
  rows.forEach(row => {
    if (!map[row.roomTypeId]) return
    row.prices.forEach(cell => {
      if (!rankIds.has(cell.priceRankId)) return
      map[row.roomTypeId][cell.priceRankId] = cell.price
    })
  })
  return map
}

export const toPricePayload = (
  amounts: BaseRateAmountMap,
  roomTypes: BaseRateRoomType[],
  ranks: PriceRank[],
): BaseRateUpdateInput['prices'] => {
  const prices: BaseRateUpdateInput['prices'] = []
  roomTypes.forEach(room => {
    ranks.forEach(rank => {
      prices.push({
        roomTypeId: room.id,
        priceRankId: rank.id,
        price: amounts[room.id]?.[rank.id] ?? null,
      })
    })
  })
  return prices
}
